// @givmo/board — the §4 line, and the counts beside it.
//
// ══ THE ONE RULE THIS COMPONENT EXISTS TO HOLD ══════════════════════════════════════════════════
//
// Plan §4: "a partial consent left incomplete stays visibly *pending*, with its signed count … — never
// silently filed" (restated 2026-10-04, CBG-FIX27: Jonathan dropped the "Not an act of the board"
// opener, so the pending sentence now reads "This consent is pending until every seat on the roster
// it was circulated to has signed. Signed: N of M."). The backend renders that sentence once, in
// `standing_statement`, so every reader of every surface gets the same words. This component puts it on screen VERBATIM and prominently while the
// consent is short of unanimity, and it is the only place either principal's pages render it.
//
// What it deliberately does NOT do:
//
//   * It never paraphrases or summarizes the sentence. A second phrasing is a second meaning, and this
//     is the one fact §4 governs.
//   * It never editorializes on the counts. "3 of 5 — nearly there" is a conclusion about an instrument
//     that is not an act of the board; "3 of 5" is arithmetic over what the organization recorded, which is
//     the sanctioned form. So the figures render as figures, beside the sentence, never inside it.
//   * It never renders a progress bar for a pending consent. A bar is a picture of "almost done", and
//     unanimity has no partial credit: four signatures out of five is not eighty per cent of an act of
//     the board, it is not an act of the board.
//
// Once the consent IS executed the emphasis drops away — the statement is still shown, but as an
// ordinary fact rather than as a warning, because at that point there is nothing outstanding to warn
// about.
//
// ══ THE COUNT IS STATED ONCE (D-CBG-18; CBG-SEC2 SEC2-WEB) ══════════════════════════════════════
//
// The backend's pending sentence ends "Signed: 4 of 7." and its executed one says "(7 of 7)", so on
// those two the figures beside the sentence said the count a second time; Jonathan's approved frames
// state it once, on the director's and the officer's pages alike. The counts line now renders only
// where the sentence carries no count of its own — a withdrawn consent's, or a status this build
// doesn't know (`board_written_consents.standing_statement`).

import { testIdProp } from '@givmo/design-system';

export interface ConsentStandingProps {
  /** The backend's own sentence. Rendered character for character. */
  statement: string;
  /** False for every status but `executed`. Drives the emphasis, not the wording. */
  isAnActOfTheBoard: boolean;
  /** The consent's status: where its sentence already states the count, the counts line is left out. */
  status?: string | null;
  /** Seats on the circulated roster that have signed, and how many there are. */
  signedCount?: number | null;
  requiredCount?: number | null;
  testId?: string;
}

/** The statuses whose backend sentence already states the signed count. */
const SENTENCE_STATES_COUNT = new Set(['pending', 'executed']);

export function ConsentStanding({
  statement,
  isAnActOfTheBoard,
  status,
  signedCount,
  requiredCount,
  testId = 'consent-standing',
}: ConsentStandingProps) {
  const required = requiredCount ?? 0;
  const signed = signedCount ?? 0;
  const showCounts = required > 0 && !SENTENCE_STATES_COUNT.has((status ?? '').toLowerCase());

  return (
    <div
      className={[
        'flex flex-col gap-8 rounded-md border p-16',
        isAnActOfTheBoard
          ? 'border-border-subtle bg-surface-sunken'
          : 'border-warning-500 bg-warning-50',
      ].join(' ')}
      {...testIdProp(testId)}
    >
      <p
        className={[
          'font-sans leading-h15 tracking-givmo',
          isAnActOfTheBoard
            ? 'text-s14 font-w400 text-text-secondary'
            : 'text-s15 font-w600 text-text-strong',
        ].join(' ')}
        data-testid={`${testId}-statement`}
      >
        {statement}
      </p>

      {showCounts ? (
        <p
          className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary"
          data-testid={`${testId}-counts`}
        >
          {signed} of {required} {required === 1 ? 'seat' : 'seats'} on the roster this went out to
          {signed === 1 ? ' has' : ' have'} signed.
        </p>
      ) : null}
    </div>
  );
}
